import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { clientAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Check, X, Download, FileText, MapPin, Calendar, Receipt,
  Briefcase, FolderOpen, DollarSign, Clock, CheckCircle, AlertTriangle, Eye,
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  draft: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  sent: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  accepted: 'bg-green-500/20 text-green-400 border-green-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  expired: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  paid: 'bg-green-500/20 text-green-400 border-green-500/30',
  overdue: 'bg-red-500/20 text-red-400 border-red-500/30',
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  on_hold: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

const fmtCurrency = (v) =>
  `$${(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const docCategoryLabels = {
  general: 'General',
  photos: 'Photos',
  client_po: 'Purchase Orders',
  quotes_variations: 'Quotes & Variations',
  reports: 'Reports',
  invoices: 'Invoices',
  certification_forms: 'Certifications & Forms',
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const ClientPortalPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeQuote, setActiveQuote] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, singleRes] = await Promise.all([
        clientAPI.getDashboard(token),
        clientAPI.getQuote(token),
      ]);
      setData(dashRes.data);
      setActiveQuote(singleRes.data?.quote || null);
      setError(null);
    } catch (e) {
      setError('We couldn\'t open this project. The link may have expired.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await clientAPI.approveQuote(token);
      toast.success('Quote approved! Your builder will be in touch shortly.');
      load();
    } catch {
      toast.error('Failed to approve quote');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await clientAPI.rejectQuote(token, rejectReason);
      toast.success('Response submitted');
      setShowRejectForm(false);
      load();
    } catch {
      toast.error('Failed to submit response');
    } finally {
      setProcessing(false);
    }
  };

  const downloadQuotePdf = async () => {
    try {
      const res = await clientAPI.downloadPdf(token);
      downloadBlob(new Blob([res.data], { type: 'application/pdf' }), `Quote-${activeQuote?.quote_number}.pdf`);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const downloadInvoicePdf = async (invoice) => {
    try {
      const res = await clientAPI.downloadInvoicePdf(token, invoice.id);
      downloadBlob(new Blob([res.data], { type: 'application/pdf' }), `Invoice-${invoice.invoice_number}.pdf`);
    } catch {
      toast.error('Failed to download invoice');
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const res = await clientAPI.downloadDocument(token, doc.id);
      const blob = new Blob([res.data], { type: doc.mime_type || 'application/octet-stream' });
      downloadBlob(blob, doc.original_filename || doc.filename);
    } catch {
      toast.error('Failed to download file');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md bg-card border-border">
          <CardContent className="p-8 text-center">
            <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Project Not Found</h2>
            <p className="text-muted-foreground">{error || 'This link is no longer valid.'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { company, job, quotes = [], invoices = [], documents = [], summary = {} } = data;
  const tokenQuote = activeQuote;
  const canRespondToToken = tokenQuote && (tokenQuote.status === 'sent' || tokenQuote.status === 'draft');

  // Group documents by type for display
  const docsByType = documents.reduce((acc, d) => {
    const k = d.document_type || 'general';
    (acc[k] = acc[k] || []).push(d);
    return acc;
  }, {});

  const acceptedQuote = quotes.find((q) => q.status === 'accepted');
  const totalApproved = acceptedQuote?.total || tokenQuote?.total || 0;

  return (
    <div className="min-h-screen bg-background" data-testid="client-portal-dashboard">
      {/* Header */}
      <header className="border-b border-border bg-card/40 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-4">
            {company?.logo_url ? (
              <img src={company.logo_url} alt={company.name} className="h-12 object-contain" />
            ) : (
              <div className="h-12 w-12 rounded bg-primary/15 text-primary flex items-center justify-center">
                <Briefcase className="h-6 w-6" />
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground" data-testid="client-portal-company-name">{company?.name || 'Your builder'}</p>
              <h1 className="text-xl md:text-2xl font-bold leading-tight" data-testid="client-portal-job-title">{job?.title}</h1>
              {job?.address && (
                <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" /> {job.address}
                </p>
              )}
            </div>
          </div>
          {job?.status && (
            <Badge className={`${statusColors[job.status] || ''} uppercase font-mono`}>
              {job.status.replace('_', ' ')}
            </Badge>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-6">
        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="client-portal-summary">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><DollarSign className="h-3 w-3" /> Project Total</div>
              <div className="text-xl font-bold font-mono mt-1">{fmtCurrency(summary.po_total || totalApproved)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">approved scope</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Receipt className="h-3 w-3" /> Invoiced</div>
              <div className="text-xl font-bold font-mono mt-1 text-primary">{fmtCurrency(summary.invoiced_to_date)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{invoices.length} invoice(s)</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><CheckCircle className="h-3 w-3" /> Paid</div>
              <div className="text-xl font-bold font-mono mt-1 text-green-400">{fmtCurrency(summary.paid_to_date)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">cash received</div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Clock className="h-3 w-3" /> Outstanding</div>
              <div className={`text-xl font-bold font-mono mt-1 ${summary.outstanding > 0 ? 'text-yellow-400' : 'text-muted-foreground'}`}>{fmtCurrency(summary.outstanding)}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">awaiting payment</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview">
          <TabsList className="bg-secondary/40">
            <TabsTrigger value="overview" data-testid="client-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="client-tab-quotes">Quotes ({quotes.length})</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="client-tab-invoices">Invoices ({invoices.length})</TabsTrigger>
            <TabsTrigger value="documents" data-testid="client-tab-documents">Documents ({documents.length})</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="space-y-4 pt-4">
            {tokenQuote && (
              <Card className="bg-card border-border" data-testid="client-active-quote-card">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground">Quote shared with you</p>
                      <CardTitle className="text-lg">{tokenQuote.quote_number}</CardTitle>
                    </div>
                    <Badge className={`${statusColors[tokenQuote.status] || ''} uppercase font-mono`}>{tokenQuote.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">Prepared for</div>
                      <div className="font-medium">{tokenQuote.client_name || job?.client_name}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Total</div>
                      <div className="font-bold font-mono text-primary">{fmtCurrency(tokenQuote.total)}</div>
                    </div>
                    {tokenQuote.valid_until && (
                      <div>
                        <div className="text-xs text-muted-foreground">Valid until</div>
                        <div className="font-medium flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{fmtDate(tokenQuote.valid_until)}</div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={downloadQuotePdf} data-testid="client-download-quote-pdf">
                      <Download className="h-4 w-4 mr-1.5" /> Download Quote
                    </Button>
                    {canRespondToToken && !showRejectForm && (
                      <>
                        <Button onClick={handleApprove} disabled={processing} className="bg-green-600 hover:bg-green-700" data-testid="client-approve-quote">
                          <Check className="h-4 w-4 mr-1.5" />
                          {processing ? 'Processing…' : 'Approve Quote'}
                        </Button>
                        <Button variant="destructive" onClick={() => setShowRejectForm(true)} data-testid="client-decline-quote">
                          <X className="h-4 w-4 mr-1.5" /> Decline
                        </Button>
                      </>
                    )}
                  </div>

                  {showRejectForm && (
                    <div className="p-4 border border-border rounded-sm space-y-3">
                      <p className="font-medium text-sm">Let your builder know why you're declining (optional):</p>
                      <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} placeholder="Optional feedback…" />
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={() => setShowRejectForm(false)}>Cancel</Button>
                        <Button variant="destructive" size="sm" onClick={handleReject} disabled={processing}>
                          {processing ? 'Submitting…' : 'Submit'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {tokenQuote.status === 'accepted' && (
                    <div className="p-3 rounded-sm bg-green-500/10 border border-green-500/30 flex items-center gap-2 text-green-400 text-sm">
                      <Check className="h-4 w-4" /> Approved — your builder has been notified.
                    </div>
                  )}
                  {tokenQuote.status === 'rejected' && (
                    <div className="p-3 rounded-sm bg-red-500/10 border border-red-500/30 text-sm text-red-400 flex items-center gap-2">
                      <X className="h-4 w-4" /> Declined — thank you for the feedback.
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {job?.description && (
              <Card className="bg-card border-border">
                <CardHeader><CardTitle className="text-base">Project Description</CardTitle></CardHeader>
                <CardContent className="text-sm text-muted-foreground whitespace-pre-line">{job.description}</CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Quotes */}
          <TabsContent value="quotes" className="pt-4">
            <Card className="bg-card border-border">
              <CardContent className="p-0">
                {quotes.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">No quotes shared with you yet.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>Number</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quotes.map((q) => (
                        <TableRow key={q.id} className="border-border" data-testid={`client-quote-row-${q.id}`}>
                          <TableCell className="font-mono text-sm">
                            {q.quote_number}
                            {q.client_token === token && (
                              <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-primary/15 text-primary rounded">shared</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={`${statusColors[q.status] || ''} uppercase font-mono`}>{q.status}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{fmtDate(q.created_at)}</TableCell>
                          <TableCell className="text-right font-mono font-bold">{fmtCurrency(q.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices */}
          <TabsContent value="invoices" className="pt-4">
            <Card className="bg-card border-border">
              <CardContent className="p-0">
                {invoices.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">No invoices yet.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>Number</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Issued</TableHead>
                        <TableHead>Due</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Outstanding</TableHead>
                        <TableHead className="w-24">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((inv) => {
                        const isOverdue = inv.status !== 'paid' && inv.due_date && new Date(inv.due_date) < new Date();
                        return (
                          <TableRow key={inv.id} className="border-border" data-testid={`client-invoice-row-${inv.id}`}>
                            <TableCell className="font-mono text-sm">
                              {inv.invoice_number}
                              {inv.progress_invoice_index ? <span className="text-[10px] ml-1.5 px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">#{inv.progress_invoice_index}</span> : null}
                              {inv.invoice_kind === 'progress' && (
                                <span className="text-[10px] ml-1.5 px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                                  Progress{inv.progress_pct ? ` ${Number(inv.progress_pct)}%` : ''}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={`${statusColors[inv.status] || ''} uppercase font-mono ${isOverdue ? 'border-red-500/40' : ''}`}>
                                {isOverdue ? 'Overdue' : inv.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{fmtDate(inv.created_at)}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{fmtDate(inv.due_date)}</TableCell>
                            <TableCell className="text-right font-mono font-bold">{fmtCurrency(inv.total)}</TableCell>
                            <TableCell className={`text-right font-mono ${(inv.amount_due || 0) > 0 ? 'text-yellow-400' : 'text-green-400'}`}>{fmtCurrency(inv.amount_due)}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="icon" onClick={() => downloadInvoicePdf(inv)} title="Download invoice PDF" data-testid={`client-download-invoice-${inv.id}`}>
                                <Download className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Progress payments */}
            {data.progress_payments?.length > 0 && (
              <Card className="bg-card border-border mt-4">
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-4 w-4 text-green-400" /> Payment History</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {data.progress_payments.map((p) => (
                    <div key={p.id || `${p.date}-${p.amount}`} className="flex items-center justify-between text-sm border-b border-border/40 pb-2 last:border-0">
                      <div className="flex items-center gap-2">
                        {p.received ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Clock className="h-4 w-4 text-muted-foreground" />}
                        <span className="text-muted-foreground">{fmtDate(p.date)}</span>
                        {p.note && <span className="text-xs text-muted-foreground italic">— {p.note}</span>}
                      </div>
                      <span className="font-mono font-bold">{fmtCurrency(p.amount)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Documents */}
          <TabsContent value="documents" className="pt-4 space-y-4">
            {documents.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <FolderOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  No documents have been shared yet.
                </CardContent>
              </Card>
            ) : (
              Object.entries(docsByType).map(([type, items]) => (
                <Card key={type} className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-base">{docCategoryLabels[type] || type}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {items.map((d) => (
                      <div key={d.id} className="flex items-center justify-between border border-border rounded-sm px-3 py-2 hover:bg-secondary/20" data-testid={`client-document-row-${d.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{d.original_filename}</div>
                            <div className="text-[11px] text-muted-foreground">{fmtDate(d.created_at)}</div>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => downloadDocument(d)} data-testid={`client-download-doc-${d.id}`}>
                          <Download className="h-4 w-4 mr-1" /> Download
                        </Button>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>

        <p className="text-center text-xs text-muted-foreground py-6">Powered by ConstructOne</p>
      </main>
    </div>
  );
};

export default ClientPortalPage;
